import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import { GlobalContext } from 'context/userContext';
import { Grid, Paper, Typography, Box, useTheme, Stack, Alert } from '@mui/material';
import { DrawerHeader } from '@components/drawer-header';
import TableExplorer from '@components/table-explorer/table_explorer';
import ImuChartModal from '@components/imu-chart-modal/imu_chart_modal';
import DateSelector from '@components/date-selector';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as S from './styles';

export function EventReports() {

  const { operation } = useContext(GlobalContext);
  const theme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const tableInstanceRef = useRef<any>(null);

  // Date selection state (default last 30 days)
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);
  const [localStartDate, setLocalStartDate] = useState<Date>(defaultStartDate);
  const [localEndDate, setLocalEndDate] = useState<Date>(defaultEndDate);

  const handleStartDateChange = (date: Date) => {
    if (!date) return;
    const diffTime = localEndDate.getTime() - date.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 90) {
      alert("O limite máximo de busca é de 90 dias por vez. Selecione um periodo mais curto.");
      return;
    }
    if (date > localEndDate) {
      alert("A data inicial não pode ser maior que a final.");
      return;
    }
    setLocalStartDate(date);
  };

  const handleEndDateChange = (date: Date) => {
    if (!date) return;
    const diffTime = date.getTime() - localStartDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 90) {
      alert("O limite máximo de busca é de 90 dias por vez. Selecione um periodo mais curto.");
      return;
    }
    if (date < localStartDate) {
      alert("A data final não pode ser menor que a inicial.");
      return;
    }
    setLocalEndDate(date);
  };

  const formattedStart = (() => {
    const d = new Date(localStartDate.getTime());
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  })();

  const formattedEnd = (() => {
    const d = new Date(localEndDate.getTime());
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  })();

  const handleRowAction = (dtRow: any) => {
    if (!tableInstanceRef.current) return;

    const dataArray = dtRow.data();
    if (!dataArray) return;

    const columns = tableInstanceRef.current.settings()[0].aoColumns;
    const rowObj: any = {};
    columns.forEach((col: any, index: number) => {
      const colName = col.sTitle || col.title || col.name || String(index);
      rowObj[colName] = dataArray[index];
    });

    if (rowObj.type === 'crash' || rowObj.type === 'crash_fwd' || rowObj.type === 'crash_rev' || rowObj.type === 'hole' || rowObj.type === 'imu_record') {
      rowObj.tid = rowObj.tid || rowObj.id;
      setSelectedEvent(rowObj);
      setModalOpen(true);
    } else {
      console.log('No IMU recording available for this event.');
    }
  };

  return (
    <DrawerHeader
      title={"Ocorrências"}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: 'background.paper',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              mb: 1
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
              <Box>
                <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Histórico de Eventos</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Analise todos os alertas gerados pela telemetria, incluindo infrações de velocidade, impactos severos e falhas sistêmicas. Utilize as evidências para campanhas de conscientização e treinamentos focados ou investigações.
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'background.default',
                p: 2,
                pt: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mr: 1, textTransform: 'uppercase' }}>
                  Período
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <DateSelector
                    label="Data Inicial"
                    value={localStartDate}
                    onDateSelect={handleStartDateChange}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>até</Typography>
                  <DateSelector
                    label="Data Final"
                    value={localEndDate}
                    onDateSelect={handleEndDateChange}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      {
        <TableExplorer
          key={`${formattedStart}-${formattedEnd}`}
          dbName={operation}
          tableName={"events"}
          headerContent={
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              px: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}>
              <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                Para visualizar o relatório de um evento de batida ou buraco, clique na linha desejada.
              </Typography>
            </Box>
          }
          startDate={formattedStart}
          endDate={formattedEnd}
          searchble={true}
          modifier_cb={handleRowAction}
          set_table_instance_cb={(table) => { tableInstanceRef.current = table; }}
          columnOrder={["stamp", "type", "setor", "id", "user", "username", "acc_h", "acc_v", "ax", "ay", "jerk_h", "svm", "lat", "lon"]}
          visibleColumns={["stamp", "type", "setor", "id", "user", "username", "acc_h", "acc_v", "ax", "ay", "jerk_h", "svm", "lat", "lon"]}
          order={[[0, "desc"]]}
        />
      }
      {selectedEvent && (
        <ImuChartModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          tid={selectedEvent.tid}
          stamp={selectedEvent.stamp}
          db={operation || ""}
          eventData={selectedEvent}
        />
      )}
    </DrawerHeader>
  );
}
